import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { CreditCard, ArrowsCounterClockwise, MagnifyingGlass, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

/**
 * MyCardHubPage — staff/sub_staff dashboard for ALL assigned cards.
 *
 * Solves the problem (user request 12-Feb-2026):
 *   "Old card ko automatically aisa system bana do ki jo unused card ho...
 *    wo data ek sath sara Aa jay... card balance k sath... or move karne
 *    ka option hona chahiye staff/sub staff k pas... qki usko pata h ki
 *    balance h ki nhi"
 *
 * Features:
 *   - Card grid with balance, agency, daily usage, last used.
 *   - "Only Unused Today" filter — daily_usage_count==0.
 *   - Search by number/holder/nickname/agency.
 *   - Per-card "Release back to admin" — admin notification + lock cleared.
 */
export default function MyCardHubPage() {
  const [cards, setCards] = useState([]);
  const [total, setTotal] = useState(0);
  const [todayIst, setTodayIst] = useState('');
  const [onlyUnused, setOnlyUnused] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [releaseDialog, setReleaseDialog] = useState(null);
  const [releaseReason, setReleaseReason] = useState('');
  const [releasing, setReleasing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (onlyUnused) params.set('only_unused', 'true');
      const { data } = await api.get(`/booking-pool/my-cards?${params}`);
      setCards(data.cards || []);
      setTotal(data.total || 0);
      setTodayIst(data.today_ist || '');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Load fail');
    } finally {
      setLoading(false);
    }
  }, [onlyUnused]);

  useEffect(() => { load(); }, [load]);

  const filtered = cards.filter(c => {
    if (!search.trim()) return true;
    const s = search.trim().toLowerCase();
    return (
      (c.card_number || '').toLowerCase().includes(s) ||
      (c.card_holder || '').toLowerCase().includes(s) ||
      (c.nickname || '').toLowerCase().includes(s) ||
      (c.linked_agency_name || '').toLowerCase().includes(s)
    );
  });

  const totalBalance = cards.reduce((sum, c) => sum + Number(c.available_limit || 0), 0);
  const unusedCount = cards.filter(c => c.is_unused_today).length;
  const unusedBalance = cards.filter(c => c.is_unused_today)
    .reduce((sum, c) => sum + Number(c.available_limit || 0), 0);

  const doRelease = async () => {
    if (!releaseDialog?.card) return;
    setReleasing(true);
    try {
      await api.post(`/booking-pool/card/${releaseDialog.card.id}/release`, {
        reason: releaseReason.trim(),
      });
      toast.success(`💳 Card released — admin ko bata diya`);
      setReleaseDialog(null);
      setReleaseReason('');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Release fail');
    } finally {
      setReleasing(false);
    }
  };

  const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');
  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Kolkata' }); }
    catch { return '—'; }
  };

  return (
    <div className="p-4 max-w-7xl mx-auto" data-testid="my-card-hub-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <CreditCard size={28} weight="fill" className="text-violet-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">My Card Hub</h1>
            <p className="text-xs text-gray-500">
              Saare assigned cards · Balance · Daily usage · Release back · IST today: {todayIst || '—'}
            </p>
          </div>
        </div>
        <Button onClick={load} variant="outline" className="gap-1" data-testid="my-card-hub-refresh">
          <ArrowsCounterClockwise size={16} /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        <div className="bg-violet-50 border-2 border-violet-300 rounded p-3" data-testid="my-card-hub-total-tile">
          <div className="text-[10px] uppercase font-bold text-violet-700">Total Cards</div>
          <div className="text-2xl font-extrabold text-violet-900">{total}</div>
        </div>
        <div className="bg-emerald-50 border-2 border-emerald-300 rounded p-3" data-testid="my-card-hub-balance-tile">
          <div className="text-[10px] uppercase font-bold text-emerald-700">Total Balance</div>
          <div className="text-2xl font-extrabold text-emerald-900">{fmt(totalBalance)}</div>
        </div>
        <button
          type="button"
          onClick={() => setOnlyUnused(v => !v)}
          className={`text-left rounded p-3 border-2 transition ${onlyUnused ? 'bg-orange-100 border-orange-500' : 'bg-orange-50 border-orange-300 hover:bg-orange-100'}`}
          data-testid="my-card-hub-unused-tile"
        >
          <div className="text-[10px] uppercase font-bold text-orange-700">⏸️ Unused Today</div>
          <div className="text-2xl font-extrabold text-orange-900">{unusedCount}</div>
          <div className="text-[9px] text-orange-700">{fmt(unusedBalance)} idle{onlyUnused ? ' · 🟢 filter ON' : ''}</div>
        </button>
        <div className="bg-blue-50 border border-blue-200 rounded p-3">
          <div className="text-[10px] uppercase font-bold text-blue-700">Showing</div>
          <div className="text-2xl font-extrabold text-blue-900">{filtered.length}</div>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={16} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search card / holder / nickname / agency…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
            data-testid="my-card-hub-search-input"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded p-8 text-center text-gray-500" data-testid="my-card-hub-empty">
          {onlyUnused ? 'Aaj koi card unused nahi hai — sab use ho rahe hain ✅' : 'Koi card assigned nahi hai. Admin se request karo.'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((c) => {
            const bal = Number(c.available_limit || 0);
            const lim = Number(c.daily_limit || 0);
            const used = Number(c.daily_usage_count || 0);
            const pctUsed = lim > 0 ? Math.min(100, (used / lim) * 100) : 0;
            const isLowBal = bal > 0 && bal < 1000;
            const isUnused = c.is_unused_today;
            return (
              <div
                key={c.id}
                className={`rounded-lg border-2 p-3 ${isUnused ? 'bg-orange-50/40 border-orange-300' : 'bg-white border-gray-200'} hover:shadow-md transition`}
                data-testid={`my-card-hub-${c.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-sm font-bold text-gray-900">
                      **** **** **** {(c.card_number || '').slice(-4)}
                    </div>
                    <div className="text-[11px] text-gray-600 truncate">{c.card_holder || c.nickname || '—'}</div>
                    {c.linked_agency_name && (
                      <div className="text-[10px] mt-0.5 inline-block px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded font-semibold">
                        🏢 {c.linked_agency_name}
                      </div>
                    )}
                  </div>
                  {isUnused && (
                    <span className="text-[9px] px-1.5 py-0.5 bg-orange-200 text-orange-900 rounded font-bold whitespace-nowrap">⏸️ Idle</span>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2 mb-2">
                  <div>
                    <div className="text-[9px] text-gray-500 uppercase">Balance</div>
                    <div className={`text-base font-extrabold ${isLowBal ? 'text-amber-700' : 'text-emerald-700'}`}>
                      {fmt(bal)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[9px] text-gray-500 uppercase">Daily Usage</div>
                    <div className="text-base font-bold text-gray-800">{used}/{lim || '∞'}</div>
                  </div>
                </div>

                {lim > 0 && (
                  <div className="h-1 bg-gray-200 rounded overflow-hidden mb-2">
                    <div className={`h-1 ${pctUsed > 80 ? 'bg-red-500' : pctUsed > 50 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${pctUsed}%` }} />
                  </div>
                )}

                <div className="text-[10px] text-gray-500 mb-2">
                  Last used: <span className="font-semibold text-gray-700">{fmtDate(c.last_used_at)}</span>
                </div>

                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setReleaseDialog({ card: c })}
                  className="w-full h-7 text-[11px] gap-1 border-rose-300 text-rose-700 hover:bg-rose-50"
                  data-testid={`my-card-hub-${c.id}-release`}
                >
                  💳 Release back to admin
                </Button>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!releaseDialog} onOpenChange={(open) => { if (!open) { setReleaseDialog(null); setReleaseReason(''); } }}>
        <DialogContent className="max-w-md" data-testid="my-card-hub-release-dialog">
          <DialogHeader>
            <DialogTitle>Release Card to Admin</DialogTitle>
          </DialogHeader>
          {releaseDialog?.card && (
            <div className="bg-gray-50 border rounded p-2 text-xs space-y-0.5">
              <div><strong>Card:</strong> ****{(releaseDialog.card.card_number || '').slice(-4)}</div>
              <div><strong>Holder:</strong> {releaseDialog.card.card_holder || '—'}</div>
              <div><strong>Balance:</strong> <span className="text-emerald-700 font-bold">{fmt(releaseDialog.card.available_limit || 0)}</span></div>
            </div>
          )}
          <div>
            <label className="text-xs font-semibold text-gray-700">Reason (optional)</label>
            <Input
              value={releaseReason}
              onChange={(e) => setReleaseReason(e.target.value)}
              placeholder="e.g. 'Need different agency card' or 'Balance khali ho gaya'"
              data-testid="my-card-hub-release-reason"
            />
          </div>
          <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
            ⚠️ Release karne ke baad ye card admin ke paas chala jayega. Aap iska use nahi kar paoge jab tak admin dubara assign na kare.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setReleaseDialog(null); setReleaseReason(''); }} disabled={releasing}>
              <X size={14} /> Cancel
            </Button>
            <Button onClick={doRelease} disabled={releasing} className="bg-rose-600 hover:bg-rose-700" data-testid="my-card-hub-release-confirm">
              {releasing ? 'Releasing…' : '💳 Confirm Release'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
